export function makeOptions(prefix, arrayOfValuesAndLabels, filterMethod) {
    const results = [];
    Object.entries(arrayOfValuesAndLabels).forEach(function ([key, value]) {
        if (filterMethod === undefined || filterMethod(value)) {
            results.push({"key": prefix + "-" + key, "value": key, "text": value.label});
        }
    });
    return results;
}

export function makeOptionsFromArray(prefix, arrayOfLabels, filterMethod) {
    const results = [];
    for (let i = 0; i < arrayOfLabels.length; i++) {
        const label = arrayOfLabels[i];
        if (filterMethod === undefined || filterMethod(label, i)) {
            results.push({"key": prefix + "-" + i, "value": i, "text": label});
        }
    }
    return results;
}

const defaultValuesForLoop = function (states, globals, loops) {
    const loop = loops[this.loopName];
    const loopState = loop?.states[loops.index - 1];
    const wasAlreadyModified = loopState && loopState[this.id];
    // if the item was already modified, we don't take the values from the first item
    if (wasAlreadyModified) {
        return;
    }
    const realFirstIteration = getRealFirstIteration(loop) || 0;
    const firstItem = loop?.states[realFirstIteration][this.id]
    const firstItemApplyToAll = firstItem?.applyToAll
    if (firstItemApplyToAll) {
        return [...firstItem.values];
    } else {
        return null;
    }
}

export class ItemDefinition {
    constructor(attributes) {
        if (attributes !== undefined) {
            const {
                id,
                title,
                fields,
                buildFields,
                buildErrors,
                buildGroup,
                buildDefaultValues,
                validate,
                chooseNext,
                loopName,
                buildLoopName,
                isDecisionNode,
                processPreliminaries,
                group,
                allowApplyAll,
                defaultValues
            } = attributes;
            this.allowApplyAll = allowApplyAll === undefined ? true : allowApplyAll;
            this.defaultValues = defaultValues;
            this.id = id;
            this.title = title;
            this.fields = fields;
            this.group = group;
            this.loopName = loopName;
            this.isDecisionNode = isDecisionNode;
            this._buildFieldsFunction = buildFields;
            this._buildLoopNameFunction = buildLoopName;
            this._buildGroupFunction = buildGroup;
            this._buildErrorsFunction = buildErrors;
            this._buildDefaultValuesFunction = buildDefaultValues;
            this._processPreliminaries = processPreliminaries;
            this._chooseNextFunction = chooseNext;
            this._validateFunction = validate;
            if ((this.loopName || this.buildLoopName) && (!this.defaultValues && !buildDefaultValues)) {
                this._buildDefaultValuesFunction = defaultValuesForLoop;
            }
        }
        this.errors = [];
    }

    validate(states, globals, loops) {
        if (this.fields.length === 0) {
            return
        }
        this.errors = [];
        if (this._validateFunction) {
            this._validateFunction(states, globals, loops);
        }
        for (let i = 0; i < this.fields.length; i++) {
            const field = this.fields[i];
            if (field.type === 'number') {
                const values = states[this.id].values;
                if (values === undefined) return;
                const value = values[i];
                if (!Number.isInteger(value)) {
                    this.errors.push({text: 'Invalid value for this field. An integer is expected.', field: i})
                } else if (field.min !== undefined && value < field.min) {
                    // `min` reaches the number input as an HTML attribute, which only
                    // constrains its spinner — a typed value below it is accepted, and the
                    // flow then relies on a bound it never enforced. numberOfParties
                    // declares min 2, and a single party leaves later items unanswerable:
                    // partyRolePR needs one party as data provider and another in a second
                    // role, and datasetSamplesReceivers offers no recipient once the only
                    // party is the samples provider. Both dead-end the flow.
                    this.errors.push({
                        text: `Invalid value for this field. A value of ${field.min} or more is expected.`,
                        field: i,
                    })
                } else if (field.max !== undefined && value > field.max) {
                    this.errors.push({
                        text: `Invalid value for this field. A value of ${field.max} or less is expected.`,
                        field: i,
                    })
                }
            }
        }
    }

    chooseNext(states, globals, loops) {
        if (this._processPreliminaries) {
            this._processPreliminaries(states, globals, loops)
        }
        return this._chooseNextFunction(states, globals, loops)
    }

    buildLoopName(states, globals, loops) {
        if (this._buildLoopNameFunction) {
            this.loopName = this._buildLoopNameFunction(states, globals, loops)
        }
    }

    initWithoutLoopName(states, globals, loops) {
        this.errors = [];
        if (this._buildGroupFunction) {
            this.group = this._buildGroupFunction(states, globals, loops);
        }
        if (this._buildErrorsFunction) {
            this.errors = this._buildErrorsFunction(states, globals, loops);
        }
        if (this.errors.length > 0) {
            this.fields = [];
        } else {
            if (this._buildFieldsFunction) {
                this.fields = this._buildFieldsFunction(states, globals, loops);
            }
            if (this._buildDefaultValuesFunction) {
                this.defaultValues = this._buildDefaultValuesFunction(states, globals, loops);
            }

        }
    }

    init(states, globals, loops) {
        this.buildLoopName(states, globals, loops);
        this.initWithoutLoopName(states, globals, loops);

    }

    getLoopIndex(loops) {
        const loop = loops[this.loopName];
        if (loop === undefined) {
            return 1;
        }
        return loop.index;
    }

    nextLoopIteration(loops) {
        const loop = loops[this.loopName];
        // needed for the transition period, sessions saved before the loop browsability feature
        if (loop.validIterations === undefined) {
            loop.validIterations = Array(loop.index);
        }
        loop.validIterations[loop.index - 1] = true;
        loop.index += 1;
        // we need to reset inner loops
        for (const loopName in loops) {
            if (Object.prototype.hasOwnProperty.call(loops, loopName)) {
                if (loopName !== this.loopName && loopName.startsWith(this.loopName)) {
                    loops[loopName].index = 1;
                }
            }
        }
        return loop.firstItemId;
    }
}


class Serializer {
    constructor(types) {
        this.types = types;
    }

    serialize(object) {
        let idx = this.types.findIndex((e) => {
            return e.name === object.constructor.name
        });
        if (idx === -1) throw "type  '" + object.constructor.name + "' not initialized";
        return JSON.stringify([idx, Object.entries(object)]);
    }

    deserialize(jstring) {
        let array = JSON.parse(jstring);
        let object = new this.types[array[0]]();
        array[1].map(e => {
            object[e[0]] = e[1];
        });
        return object;
    }
}

export const serializer = new Serializer([ItemDefinition]);

export function isDecisionOnlyIteration(loop, iteration) {
    const iterationStates = loop.states[iteration];
    for (let itemId in iterationStates) {
        if (iterationStates.hasOwnProperty(itemId)) {
            const itemState = iterationStates[itemId];
            if (itemState && 'values' in itemState) {
                return false;
            }
        }
    }
    // all items have no value, we are in a iteration that was purely based on decision nodes, we hide
    return true;
}

export function getRealFirstIteration(loop) {
    for (let i = 0; i < loop?.states.length; i++) {
        if (!isDecisionOnlyIteration(loop, i)) {
            return i;
        }
    }
    // if none found, we consider last one is real (because not filled yet)
    return loop?.states.length - 1;
}

export function isRealFirstIteration(loop, loopIndex) {
    return loopIndex - 1 === getRealFirstIteration(loop);

}